import type { Style } from '../style/Style';

export interface RendererOptions {
  symbol?: Style;
}

export class SimpleRenderer {
  public symbol: Style;

  constructor(options: RendererOptions) {
    this.symbol = options.symbol!;
  }
}

export interface UniqueValueInfo {
  value: string | number;
  symbol: Style;
  label?: string;
}

export class UniqueValueRenderer {
  public field: string;
  public defaultSymbol: Style;
  public uniqueValueInfos: UniqueValueInfo[];

  constructor(options: {
    field: string;
    defaultSymbol: Style;
    uniqueValueInfos: UniqueValueInfo[];
  }) {
    this.field = options.field;
    this.defaultSymbol = options.defaultSymbol;
    this.uniqueValueInfos = options.uniqueValueInfos;
  }

  getStyleForFeature(feature: any): Style {
    const value = feature.get?.(this.field) ?? feature.properties?.[this.field];
    const info = this.uniqueValueInfos.find((i) => i.value === value);
    return info?.symbol || this.defaultSymbol;
  }
}

export interface ClassBreakInfo {
  minValue: number;
  maxValue: number;
  symbol: Style;
  label?: string;
}

export class ClassBreaksRenderer {
  public field: string;
  public defaultSymbol: Style;
  public classBreakInfos: ClassBreakInfo[];

  constructor(options: {
    field: string;
    defaultSymbol: Style;
    classBreakInfos: ClassBreakInfo[];
  }) {
    this.field = options.field;
    this.defaultSymbol = options.defaultSymbol;
    this.classBreakInfos = options.classBreakInfos;
  }

  getStyleForFeature(feature: any): Style {
    const value = feature.get?.(this.field) ?? feature.properties?.[this.field];
    if (typeof value !== 'number') return this.defaultSymbol;
    const info = this.classBreakInfos.find(
      (i) => value >= i.minValue && value < i.maxValue
    );
    return info?.symbol || this.defaultSymbol;
  }
}

export class HeatmapRenderer {
  public field?: string;
  public colorStops: Array<{ ratio: number; color: string }>;
  public radius: number;

  constructor(options: {
    field?: string;
    colorStops: Array<{ ratio: number; color: string }>;
    radius?: number;
    maxIntensity?: number;
    minIntensity?: number;
  }) {
    this.field = options.field;
    this.colorStops = options.colorStops;
    this.radius = options.radius || 10;
  }
}

export class DotDensityRenderer {
  public attributes: Array<{ field: string; color: string; label?: string }>;
  public dotValue: number;
  public dotSize: number;

  constructor(options: {
    attributes: Array<{ field: string; color: string; label?: string }>;
    dotValue: number;
    dotSize?: number;
  }) {
    this.attributes = options.attributes;
    this.dotValue = options.dotValue;
    this.dotSize = options.dotSize || 1;
  }
}
